import { useEffect, useState } from "react";
import { careers } from "../lib/api";
import { Briefcase, Heart, Users, Award, Utensils, Clock, MapPin, ChevronRight, X, Check } from "lucide-react";
import { toast } from "sonner";
import { PHOTO_DANCEFLOOR } from "../lib/assets";

const DEPARTMENTS = [
  { key: "all", label: "All" },
  { key: "FOH", label: "Front of House" },
  { key: "BOH", label: "Back of House" },
  { key: "Security", label: "Security" },
  { key: "Music", label: "Music" },
  { key: "Management", label: "Management" },
];

const BENEFITS = [
  { icon: Award, label: "Competitive hourly + tips / salary by role" },
  { icon: Briefcase, label: "Opening-team equity in your career — first-team training" },
  { icon: Utensils, label: "Health-conscious team meals nightly" },
  { icon: Users, label: "Mentorship from a hospitality-veteran leadership team" },
  { icon: Heart, label: "Discounted dining at partner DC venues" },
  { icon: Clock, label: "Flexible scheduling that respects your life outside work" },
];

export default function CareersPage() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    careers.positions(filter).then((data) => { setPositions(data); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  const openApply = (position) => {
    setSelectedPosition(position);
    setApplyOpen(true);
  };

  return (
    <main data-testid="careers-page" className="bg-white min-h-screen pt-20 md:pt-24">
      {/* HERO */}
      <section className="relative h-[64vh] min-h-[520px] overflow-hidden bg-ink">
        <img src={PHOTO_DANCEFLOOR} alt="Onyx team" className="absolute inset-0 w-full h-full object-cover opacity-60" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/95" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-16">
          <span className="eyebrow mb-3" style={{ color: "var(--onyx-gold-soft)" }}>— We're Hiring · Opening Team</span>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl text-white">
            Build the rooftop nightlife <span className="text-[var(--onyx-gold)]">DC hasn't had yet</span>.
          </h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg leading-relaxed">
            We're assembling the founding team for Onyx Rooftop Lounge — bartenders, servers, hosts, DJs, chefs, security and more. If you take hospitality seriously and want to be part of something elevated from day one, we want to hear from you.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/8 bg-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-3 divide-x divide-black/10">
          <Stat label="Opening Team" value="15+" />
          <Stat label="Open Roles" value={positions.length || "8"} />
          <Stat label="Location" value="Dupont · DC" sub="1815 M Street NW" />
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-24">
        <span className="eyebrow">— Why Onyx</span>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-ink max-w-2xl leading-tight">Hospitality that takes care of its own.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 mt-14 border border-black/10">
          {BENEFITS.map((b, i) => (
            <div key={i} data-testid={`benefit-${i}`} className="bg-white p-8 flex items-start gap-5">
              <div className="w-11 h-11 border border-[var(--onyx-gold)] flex items-center justify-center flex-shrink-0 text-[var(--onyx-gold)]">
                <b.icon size={18} strokeWidth={1.5} />
              </div>
              <p className="leading-relaxed" style={{ color: "#1F1F1F" }}>{b.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POSITIONS */}
      <section className="bg-cream border-y border-black/8">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="eyebrow">— Open Positions</span>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-ink leading-tight">Find your seat at the bar.</h2>
            </div>
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="dept-filter">
            {DEPARTMENTS.map((d) => (
              <button
                key={d.key}
                data-testid={`filter-${d.key}`}
                onClick={() => setFilter(d.key)}
                className={`px-5 py-2.5 text-[11px] tracking-luxe uppercase border transition ${
                  filter === d.key ? "border-ink bg-ink text-white" : "border-black/15 text-black/65 hover:border-ink hover:text-ink"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-muted italic">Loading positions…</p>
          ) : positions.length === 0 ? (
            <p className="text-muted italic">No open positions in this department right now.</p>
          ) : (
            <div className="divide-y divide-black/10 border-y border-black/10 bg-white">
              {positions.map((p) => (
                <button
                  key={p.id}
                  data-testid={`position-${p.id}`}
                  onClick={() => openApply(p)}
                  className="group w-full text-left px-6 md:px-8 py-7 md:py-8 grid grid-cols-12 gap-4 md:gap-8 items-center hover:bg-cream transition"
                >
                  <div className="col-span-12 md:col-span-7">
                    <div className="flex items-center gap-3 text-[10px] tracking-luxe uppercase text-gold mb-2">
                      <span>{p.department}</span>
                      <span className="text-black/20">·</span>
                      <span>{p.type}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight">{p.title}</h3>
                    <p className="text-muted text-sm mt-2 leading-relaxed line-clamp-2">{p.summary}</p>
                  </div>
                  <div className="col-span-12 md:col-span-5 flex md:justify-end items-center">
                    <span className="flex items-center gap-3 text-[11px] tracking-luxe uppercase text-ink group-hover:text-gold transition">
                      Apply Now <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GENERAL APP CTA */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-24 text-center">
        <p className="eyebrow mb-3">— Don't see your role?</p>
        <h3 className="font-display text-3xl md:text-4xl text-ink max-w-2xl mx-auto leading-tight">Send us a note anyway.</h3>
        <p className="text-muted mt-4 max-w-xl mx-auto">We're always meeting strong hospitality professionals. Tell us where you fit.</p>
        <button
          data-testid="general-apply-cta"
          onClick={() => openApply({ title: "General Application", department: "Other", type: "Open" })}
          className="btn-luxe btn-luxe-gold mt-10"
        >
          Submit General Application
        </button>
      </section>

      {/* CONTACT */}
      <section className="bg-ink text-white py-16">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <p className="eyebrow" style={{ color: "var(--onyx-gold-soft)" }}>— Questions</p>
            <p className="font-display text-2xl mt-2">info@onyxrooftopdc.com</p>
          </div>
          <div className="flex items-center gap-2 text-white/65 text-sm">
            <MapPin size={14} />
            1815 M Street NW, Washington, DC 20036
          </div>
        </div>
      </section>

      {applyOpen && selectedPosition && (
        <ApplyModal
          position={selectedPosition}
          onClose={() => { setApplyOpen(false); setSelectedPosition(null); }}
        />
      )}
    </main>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="px-6 md:px-10 py-12 text-center">
      <p className="font-display text-5xl md:text-6xl text-ink leading-none">{value}</p>
      <p className="text-[10px] tracking-luxe uppercase text-gold mt-3">{label}</p>
      {sub && <p className="text-[10px] tracking-luxe-tight uppercase text-muted mt-1">{sub}</p>}
    </div>
  );
}

function ApplyModal({ position, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: position.title,
    department: position.department,
    experience_years: 0,
    resume_url: "",
    portfolio_url: "",
    cover_note: "",
    availability: "",
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await careers.apply({ ...form, experience_years: parseInt(form.experience_years) || 0 });
      setConfirmed(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="apply-modal"
      className="fixed inset-0 z-[200] bg-black/70 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start md:items-center justify-center p-4 md:p-8">
        <div
          className="bg-white w-full max-w-2xl relative shadow-2xl my-auto"
          onClick={(e) => e.stopPropagation()}
        >
        <button
          data-testid="apply-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 border border-black/15 text-black/65 hover:text-ink hover:border-ink flex items-center justify-center transition z-10"
        >
          <X size={18} />
        </button>

        {confirmed ? (
          <div className="p-10 md:p-14 text-center">
            <div className="w-14 h-14 mx-auto rounded-full border-2 border-[var(--onyx-gold)] text-[var(--onyx-gold)] flex items-center justify-center mb-5">
              <Check size={24} />
            </div>
            <p className="eyebrow mb-2">— Application Received</p>
            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">Thanks, {form.full_name.split(" ")[0] || "there"}.</h3>
            <p className="text-muted max-w-md mx-auto mt-4">
              We've received your application for <span className="text-ink font-medium">{position.title}</span>. Our hiring team reviews every submission — we'll be in touch within 5–7 days.
            </p>
            <button onClick={onClose} className="btn-luxe btn-luxe-ghost mt-8" data-testid="apply-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-10">
            <p className="eyebrow">— Apply</p>
            <h3 className="font-display text-2xl md:text-3xl text-ink mt-2 leading-tight">{position.title}</h3>
            <p className="text-muted text-sm mt-1">{position.department} · {position.type}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-7">
              <Field label="Full Name *">
                <input data-testid="apply-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Phone *">
                <input data-testid="apply-phone" required placeholder="(202) 878-8151" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Email *" full>
                <input data-testid="apply-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Years of Experience">
                <input data-testid="apply-experience" type="number" min="0" max="50" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Availability">
                <input data-testid="apply-availability" placeholder="Weekends, evenings, etc." value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Resume URL (Drive, Dropbox, LinkedIn)" full>
                <input data-testid="apply-resume" placeholder="https://" value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Portfolio / Mix / Instagram" full hint="DJs and creatives — link your work">
                <input data-testid="apply-portfolio" placeholder="https://" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} className="onyx-input" />
              </Field>
              <Field label="Why Onyx?" full hint="2–3 sentences is plenty">
                <textarea data-testid="apply-cover" rows={4} value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })} className="onyx-input resize-none" />
              </Field>
            </div>

            <button
              data-testid="apply-submit"
              type="submit"
              disabled={submitting}
              className="btn-luxe btn-luxe-gold w-full mt-8 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>

            <p className="text-[10px] text-muted text-center mt-4">
              We review every application personally. Equal opportunity employer.
            </p>

            <style>{`
              .onyx-input {
                width: 100%;
                background: #fff;
                border: 1px solid rgba(0,0,0,0.15);
                color: #111;
                padding: 0.85rem 1rem;
                font-family: 'TikTok Sans', sans-serif;
                font-size: 0.95rem;
                transition: border-color 220ms;
              }
              .onyx-input:focus { outline: none; border-color: var(--onyx-gold); }
              .onyx-input::placeholder { color: rgba(0,0,0,0.35); }
            `}</style>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] tracking-luxe uppercase text-black/60 mb-2">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-muted mt-1.5">{hint}</p>}
    </div>
  );
}
